import React from 'react';
import { Box, Container, Typography, Grid, Divider, Stack } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import GavelIcon from '@mui/icons-material/Gavel';
import ShieldIcon from '@mui/icons-material/Shield';

export default function PrivacyPolicy() {
  const privacyIcon = (
    <LockIcon 
      sx={{ 
        fontSize: 40,
        color: "#f0f0f0" 
      }} 
    />
  );

  const securityIcon = (
    <SecurityIcon 
      sx={{ 
        fontSize: 40,
        color: "#f0f0f0" 
      }} 
    />
  );

  const legalIcon = (
    <GavelIcon 
      sx={{ 
        fontSize: 40,
        color: "#f0f0f0" 
      }} 
    />
  );

  const protectionIcon = (
    <ShieldIcon 
      sx={{ 
        fontSize: 40,
        color: "#f0f0f0" 
      }} 
    />
  );

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#080808",
        py: 10,
        position: "relative",
        color: "#e4e2e2"
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {privacyIcon}
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography
              variant="h3"
              sx={{
                color: "#fefdfd",
                fontFamily: "'Spline Sans Mono', monospace",
                fontWeight: 600,
                fontStyle: "italic",
                fontSize: { xs: "24px", md: "32px" },
                lineHeight: "1.2",
                mb: 3
              }}
            >
              Privacy Policy
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#7a7976",
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "15px",
                lineHeight: "29.1px",
              }}
            >
              Last Updated: April 9, 2025
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ bgcolor: "#333", mb: 6 }} />

        {/* Introduction */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            1. Introduction
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
            }}
          >
            Welcome to our Secure Data Transfer Service. We respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
          </Typography>
        </Box>

        {/* Information Collection */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            2. Information We Collect
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: "#e4e2e2",
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 500,
              fontSize: "18px",
              mb: 2
            }}
          >
            2.1 Personal Information
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            We may collect the following types of personal information:
          </Typography>
          <Box component="ul" sx={{ color: "#7a7976", pl: 4, mb: 3 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Name, email address, phone number, and billing information</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Account credentials</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>IP address and device information</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Usage data related to our services</Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>Customer service communications</Typography>
          </Box>
          
          <Typography
            variant="h6"
            sx={{
              color: "#e4e2e2",
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 500,
              fontSize: "18px",
              mb: 2
            }}
          >
            2.2 Files and Data Content
          </Typography>
          <Box component="ul" sx={{ color: "#7a7976", pl: 4 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Files you upload, transfer, or store through our service</Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>Metadata associated with your files</Typography>
          </Box>
        </Box>

        {/* Feature cards */}
        <Grid container spacing={4} sx={{ mb: 6, display: "flex", flexWrap: {xs: "wrap", sm: "nowrap"} }}>
          <Grid item xs={12} md={4}>
            <Stack alignItems="center" spacing={2} sx={{ p: 3, bgcolor: "#111", borderRadius: 2, height: "100%" }}>
              <Box sx={{ height: 80, display: "flex", alignItems: "center" }}>
                {securityIcon}
              </Box>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "#e4e2e2",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 500,
                  fontSize: "21px",
                  lineHeight: "25.2px",
                }}
              >
                Data Security
              </Typography>
              <Typography
                variant="body2"
                align="center"
                sx={{
                  color: "#71706b",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}
              >
                We implement encryption of data in transit and at rest, with regular security assessments and access controls to protect your information.
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack alignItems="center" spacing={2} sx={{ p: 3, bgcolor: "#111", borderRadius: 2, height: "100%" }}>
              <Box sx={{ height: 80, display: "flex", alignItems: "center" }}>
                {protectionIcon}
              </Box>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "#e4e2e2",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 500,
                  fontSize: "21px",
                  lineHeight: "25.2px",
                }}
              >
                Your Rights
              </Typography>
              <Typography
                variant="body2"
                align="center"
                sx={{
                  color: "#71706b",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}
              >
                You have the right to access, correct, delete your personal information, and restrict or object to processing of your data.
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack alignItems="center" spacing={2} sx={{ p: 3, bgcolor: "#111", borderRadius: 2, height: "100%" }}>
              <Box sx={{ height: 80, display: "flex", alignItems: "center" }}>
                {legalIcon}
              </Box>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "#e4e2e2",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 500,
                  fontSize: "21px",
                  lineHeight: "25.2px",
                }}
              >
                Legal Compliance
              </Typography>
              <Typography
                variant="body2"
                align="center"
                sx={{
                  color: "#71706b",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}
              >
                Our privacy practices comply with GDPR, CCPA, and other applicable privacy laws to ensure proper handling of your data.
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        {/* Rest of the privacy policy */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            3. How We Collect Information
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            We collect information:
          </Typography>
          <Box component="ul" sx={{ color: "#7a7976", pl: 4 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Directly from you when you register, subscribe, or use our services</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Automatically through cookies and similar technologies when you visit our website</Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>From third parties when you use integrated services</Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            4. How We Use Your Information
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            We use your information to:
          </Typography>
          <Box component="ul" sx={{ color: "#7a7976", pl: 4 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Provide, maintain, and improve our services</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Process transactions and send related information</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Respond to comments, questions, and customer service requests</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Send you technical notices, updates, and security alerts</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Monitor and analyze usage trends</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Detect, prevent, and address technical issues</Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>Comply with legal obligations</Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            5. Data Security
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            We implement appropriate security measures to protect your personal information and files, including:
          </Typography>
          <Box component="ul" sx={{ color: "#7a7976", pl: 4 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Encryption of data in transit and at rest</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Regular security assessments</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Access controls and authentication procedures</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Secure server infrastructure</Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>Regular backups</Typography>
          </Box>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 6, p: 4, bgcolor: "#111", borderRadius: 2 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            Contact Us
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            If you have any questions about this Privacy Policy, please contact us at:
          </Typography>
          <Box sx={{ color: "#7a7976", pl: 2 }}>
            <Typography sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Email: info@arkyn-tech.com</Typography>
            <Typography sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Phone: +421 952307969</Typography>
            <Typography sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>Address: Kostolný rad 318/15 Kráľovský Chlmec 077 01</Typography>
          </Box>
        </Box>

        <Divider sx={{ bgcolor: "#333", mb: 4 }} />
        
        <Typography
          variant="body2"
          align="center"
          sx={{
            color: "#71706b",
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "14px",
            mb: 2
          }}
        >
          © 2025 Arkyn-tech. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}